import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { withServerProps } from '@web/library/with-server-props';
import { useTranslation } from 'react-i18next';
import {
  IndexListingsScreen,
  IndexListingsScreenProps,
  listingPageTranslationPaths,
} from 'shared/screens/listings/index-listings';
import { Page } from '@web/components/page';
import { PageBuilder } from 'shared/library/page-builder';
import { Animal, Breed } from 'shared/@types';
import {
  getListingsFilterQuery,
  ListingsFilter,
  shouldNoIndexListingsFilter,
} from 'shared/state/listings-filter';
import { useLocale } from 'shared/library/i18n/use-locale';
import { View } from 'react-native';
import { H2 } from 'shared/components/title';
import { Button } from 'shared/components/button';

import { Image } from 'shared/components/image';
import { useRoute } from 'shared/library/routes/route';
import { useRouter } from 'shared/library/use-router';
import { compactBreedForListingsPage } from '@web/library/compact-breed';
import { loadBreedListingsPageData } from 'shared/library/breed-listings-page-data';
import { getCanonicalBreedPageRedirect } from 'shared/library/canonical-breed-page';
import { useListingIndexResults } from '@web/library/use-listing-index-results';

interface IndexBreedListingsScreenProps extends IndexListingsScreenProps {
  lockedBreed: Breed;
}

export default function IndexBreedListings(serverProps: IndexBreedListingsScreenProps) {
  const props = useListingIndexResults(serverProps, { breeds: [serverProps.lockedBreed.id] });
  const { t } = useTranslation('screens');
  const { market } = useLocale();
  const route = useRoute();
  const router = useRouter();

  const title = t('listings.index_listings.locked_breed_title', { breed: props.lockedBreed!.name });

  const description = t('listings.index_listings.locked_breed_description', {
    breed: props.lockedBreed!.name,
    country: market.country_name,
    youngAnimals: t(`common:enums.animal.${props.lockedBreed!.animal}_young_other`).toLowerCase(),
    adultAnimals: t(`common:enums.animal.${props.lockedBreed!.animal}_other`).toLowerCase(),
  });
  const buyingGuideRouteName =
    props.lockedBreed.animal === Animal.Dog ? 'buying-a-dog' : 'buying-a-cat';
  const buyingGuideTitle = t(
    props.lockedBreed.animal === Animal.Dog
      ? 'web:layout.footer.sections.dogs.buying_a_dog'
      : 'web:layout.footer.sections.cats.buying_a_cat',
  );
  const queryPathname = route('breeds.show.listings', { breed: props.lockedBreed.slug });
  const queryDefaults: Partial<ListingsFilter> = { breeds: [props.lockedBreed.id] };

  function currentFilterQuery(currentFilter?: ListingsFilter) {
    return getListingsFilterQuery(
      { ...(currentFilter ?? props.initialFilter), breeds: null, page: 1 },
      queryDefaults,
    );
  }

  const shouldNoIndex = shouldNoIndexListingsFilter(props.initialFilter, ['breeds']);

  return (
    <Page
      {...props}
      register={(seo) => {
        const pageSeo = seo
          .setTitle(title)
          .setDescription(description)
          .setPagination(props.initialListings);

        return shouldNoIndex ? pageSeo.setNoIndex() : pageSeo;
      }}
    >
      <IndexListingsScreen
        {...props}
        title={title}
        subtitle={description}
        queryPathname={queryPathname}
        queryDefaults={queryDefaults}
        onSelectBreed={(breed, currentFilter) =>
          router.push(
            route(
              'breeds.show.listings',
              { breed: breed.slug },
              {
                query: currentFilterQuery(currentFilter),
              },
            ),
          )
        }
        onSelectBreedQuery={(query, currentFilter) =>
          router.push(
            route('listings.index', undefined, {
              query: { ...currentFilterQuery(currentFilter), breeds: query },
            }),
          )
        }
        onClearBreedSelection={(currentFilter) => {
          const categoryRoute =
            props.lockedBreed.animal === Animal.Dog ? 'listings.dogs' : 'listings.cats';

          router.push(
            route(categoryRoute, undefined, {
              query: currentFilter
                ? getListingsFilterQuery(
                    { ...currentFilter, breeds: null, page: 1 },
                    { animal: props.lockedBreed.animal },
                  )
                : undefined,
            }),
          );
        }}
        initialBreeds={[props.lockedBreed]}
      />

      <View className={'border-brand-brown-dark/15 border-t bg-white p-3 md:p-6 md:py-12'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <View className={'gap-6 md:flex-row md:items-start'}>
            <Image
              src={props.lockedBreed.avatar}
              alt={props.lockedBreed.name}
              containerClassName={'aspect-4/3 w-full md:w-2/5'}
              imageClassName={'rounded'}
              sizeType={'card'}
            />

            <View className={'max-w-prose flex-1'}>
              <H2
                title={t('listings.index_listings.buying_locked_breed_title', {
                  breed: props.lockedBreed.name,
                })}
                subtitle={t('listings.index_listings.buying_locked_breed_description', {
                  breed: props.lockedBreed.name,
                })}
                marginTop={false}
              />

              <View className={'flex-row flex-wrap gap-3'}>
                <Button
                  href={route('breeds.show', { breed: props.lockedBreed.slug })}
                  text={t('listings.index_listings.buying_locked_breed_cta', {
                    breed: props.lockedBreed.name,
                  })}
                  icon={falAngleRight}
                />
                <Button
                  href={route(buyingGuideRouteName)}
                  text={buyingGuideTitle}
                  icon={falAngleRight}
                  color={'white'}
                />
              </View>

              {props.lockedBreed.breed_questions.map((question) => (
                <H2 key={question.id} title={question.question} subtitle={question.answer} />
              ))}
            </View>
          </View>
        </View>
      </View>
    </Page>
  );
}

const pageBuilder = new PageBuilder<
  IndexBreedListingsScreenProps,
  ListingsFilter & { breed: string }
>()
  .translationPaths(listingPageTranslationPaths)
  .request(({ req, params }) => loadBreedListingsPageData(req, params))
  .transformProps(({ props, params, ctx }) => {
    const redirect = getCanonicalBreedPageRedirect({
      breed: props.lockedBreed,
      requestedSlug: params.breed,
      locale: ctx.locale,
      hasWebResponse: Boolean(ctx.res),
      page: { name: 'breeds.show.listings' },
    });

    if (redirect) {
      return redirect;
    }

    return {
      ...props,
      lockedBreed: compactBreedForListingsPage(props.lockedBreed),
      initialFilter: { ...props.initialFilter, breeds: [props.lockedBreed.id] },
    };
  })
  .deriveRouteParams((props) => ({
    breed: props.lockedBreed.slug,
  }));

export const getServerSideProps = withServerProps(pageBuilder);
